/**
 * AdminErrors (/admin/errors): failed and partial scan jobs for morning
 * triage, on mockup carbon/01's brief and carbon/02's table.
 *
 *   1. The brief (BriefHero): the tenant to start with (the one with the
 *      most failed or partial jobs), with its primary button opening that
 *      tenant's jobs in the table.
 *   2. Four tiles in a condensed grid: errors, failed, partial, tenants.
 *   3. "Errors by tenant": a DataTable of tenants whose rows expand in place
 *      into a structured list of their jobs (time, message, type, status).
 *      The toolbar holds the time range, Refresh, and Expand / Collapse all.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatDistanceToNow, format } from 'date-fns'
import type { ActionBrief } from '@/lib/brief-shape'
import { BigNumber } from '@/components/carbon/BigNumber'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Button } from '@/components/carbon/Button'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IconArrowRight } from '@/components/carbon/icons'
import { Spinner } from '@/components/carbon/Spinner'
import { StructuredList } from '@/components/carbon/StructuredList'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { formatClock, jobTypeLabel } from '@/components/AdminPortal/admin-format'
import { composeScanErrorsBrief } from '@/components/AdminPortal/page-briefs'

interface ScanError {
  id: string
  jobType: string
  status: string
  statusMessage?: string
  progressPercentage: number
  currentStep?: string
  createdAt: string
  startedAt?: string
  completedAt?: string
  errorMessage?: string
  createdBy: string
}

interface TenantErrorGroup {
  tenantId: string
  tenantName: string
  tenantDomain: string
  errorCount: number
  failedCount: number
  partialCount: number
  jobs: ScanError[]
}

interface ScanErrorsResponse {
  summary: {
    totalErrors: number
    totalFailed: number
    totalPartial: number
    tenantsAffected: number
    hoursBack: number
  }
  tenantGroups: TenantErrorGroup[]
  timestamp: string
}

const RANGES = [
  { value: '12', label: 'Last 12 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '48', label: 'Last 48 hours' },
  { value: '72', label: 'Last 72 hours' },
]

const LOAD_FAILED_BRIEF: ActionBrief = {
  headline: 'Reload the page to see the scan errors.',
  lede: '**The scan errors did not load**, so Guardian cannot show which scans failed.',
}

export default function AdminErrors() {
  const [hoursBack, setHoursBack] = useState(24)
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set())

  const { data, error, isLoading, refetch, isFetching } = useQuery<ScanErrorsResponse>({
    queryKey: ['admin-scan-errors', hoursBack],
    queryFn: () => api.getAdminScanErrors(hoursBack),
    refetchInterval: 60000 // Refresh every minute
  })

  const toggleTenant = (tenantId: string) => {
    const newExpanded = new Set(expandedTenants)
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId)
    } else {
      newExpanded.add(tenantId)
    }
    setExpandedTenants(newExpanded)
  }

  const expandAll = () => {
    if (data?.tenantGroups) {
      setExpandedTenants(new Set(data.tenantGroups.map(g => g.tenantId)))
    }
  }

  const collapseAll = () => {
    setExpandedTenants(new Set())
  }

  // Status reads as a word on a neutral tag: a failed job is the strong neutral, a partial one the default.
  const getStatusBadge = (status: string) => {
    if (status === 'Failed') {
      return <Tag kind="high-contrast">Failed</Tag>
    }
    return <Tag>Partial</Tag>
  }

  const getJobTypeBadge = (jobType: string) => (
    <Tag kind="outline" size="sm">
      {jobTypeLabel(jobType)}
    </Tag>
  )

  // The table reports every expansion change as the full list; apply it through the one-row toggle.
  const handleExpandedChange = (ids: string[]) => {
    const next = new Set(ids)
    const changed = [...new Set([...ids, ...expandedTenants])].filter(id => next.has(id) !== expandedTenants.has(id))
    if (changed.length === 1) toggleTenant(changed[0])
    else setExpandedTenants(next)
  }

  const groups = data?.tenantGroups ?? []
  // The tenant with the most failed or partial jobs; ties keep the server's order.
  const top = [...groups].sort((a, b) => b.errorCount - a.errorCount)[0]

  const brief: ActionBrief = error
    ? LOAD_FAILED_BRIEF
    : composeScanErrorsBrief({
        totalErrors: data?.summary.totalErrors ?? 0,
        hoursBack: data?.summary.hoursBack ?? hoursBack,
        tenantsAffected: data?.summary.tenantsAffected ?? groups.length,
        top: top ? { name: top.tenantName, count: top.errorCount } : undefined,
      })

  const reviewTop = () => {
    if (!top) return
    if (!expandedTenants.has(top.tenantId)) toggleTenant(top.tenantId)
    requestAnimationFrame(() => document.getElementById('scan-errors')?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
  }

  const columns: DataColumn<TenantErrorGroup>[] = [
    {
      id: 'tenant',
      header: 'Tenant',
      primary: true,
      cell: group => <TableItem title={group.tenantName} sub={group.tenantDomain || group.tenantId} />,
    },
    {
      id: 'failed',
      header: 'Failed',
      inlineOnPhone: true,
      cell: group => (group.failedCount > 0 ? `${group.failedCount} failed` : <span className="text-text-helper">None failed</span>),
    },
    {
      id: 'partial',
      header: 'Partial',
      inlineOnPhone: true,
      cell: group => (group.partialCount > 0 ? `${group.partialCount} partial` : <span className="text-text-helper">None partial</span>),
    },
  ]

  const renderJobs = (group: TenantErrorGroup) => (
    <StructuredList
      aria-label={`Failed and partial jobs for ${group.tenantName}`}
      rows={group.jobs.map(job => ({
        id: job.id,
        time: format(new Date(job.createdAt), 'd MMM, HH:mm'),
        title: <span className="[overflow-wrap:anywhere]">{job.errorMessage || job.statusMessage || 'No error details available'}</span>,
        sub: (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {getJobTypeBadge(job.jobType)}
            <span>
              {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
              {job.currentStep ? ` · Step: ${job.currentStep}` : ''} · by {job.createdBy}
            </span>
          </span>
        ),
        tag: getStatusBadge(job.status),
      }))}
    />
  )

  const allOpen = groups.length > 0 && groups.every(g => expandedTenants.has(g.tenantId))
  const summary = data?.summary

  return (
    <div>
      <section aria-labelledby="errors-verdict" className="mt-8">
        <BriefHero
          brief={brief}
          size="pillar"
          headingId="errors-verdict"
          isLoading={isLoading}
          provenance={data ? `Checked by Guardian at ${formatClock(new Date(data.timestamp))} · last ${data.summary.hoursBack} hours` : undefined}
          secondaryAction={
            error ? (
              <Button kind="tertiary" onClick={() => refetch()}>
                Retry
              </Button>
            ) : top ? (
              <Button kind="primary" icon={<IconArrowRight />} onClick={reviewTop}>
                {`Review ${top.tenantName}`}
              </Button>
            ) : undefined
          }
        />
      </section>

      {error ? (
        <InlineNotification kind="error" title="Scan errors did not load." className="mt-12">
          {(error as Error).message}
        </InlineNotification>
      ) : (
        <>
          <CondensedGrid as="section" columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }} className="mt-8" aria-label="Scan errors at a glance">
            <Tile>
              <TileHeader title="Errors" />
              <BigNumber value={isLoading ? null : summary?.totalErrors ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">In the last {hoursBack} hours.</p>
            </Tile>
            <Tile>
              <TileHeader title="Failed" />
              <BigNumber value={isLoading ? null : summary?.totalFailed ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">Jobs that stopped with an error.</p>
            </Tile>
            <Tile>
              <TileHeader title="Partial" />
              <BigNumber value={isLoading ? null : summary?.totalPartial ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">Jobs that finished with gaps.</p>
            </Tile>
            <Tile>
              <TileHeader title="Tenants affected" />
              <BigNumber value={isLoading ? null : summary?.tenantsAffected ?? 0} size="figure-lg" className="mt-4" />
              <p className="m-0 mt-2 text-helper text-text-helper">With at least one failed or partial job.</p>
            </Tile>
          </CondensedGrid>

          <DataTable
            id="scan-errors"
            className="mt-12 scroll-mt-4"
            title="Errors by tenant"
            description="Failed and partial scan jobs, grouped by tenant. Open a tenant to read its jobs."
            columns={columns}
            rows={groups}
            rowId={group => group.tenantId}
            rowLabel={group => group.tenantName}
            isLoading={isLoading}
            loadingRows={3}
            expandable={{
              render: renderJobs,
              expandedIds: [...expandedTenants],
              onExpandedChange: handleExpandedChange,
            }}
            toolbar={
              <>
                <TableSelect
                  label="Time range"
                  value={String(hoursBack)}
                  options={RANGES}
                  onChange={value => setHoursBack(Number(value))}
                  className="c-md:min-w-[16rem]"
                />
                <div className="ml-auto flex flex-none flex-wrap">
                  <Button kind="ghost" onClick={() => refetch()} disabled={isFetching} icon={isFetching ? <Spinner /> : undefined}>
                    Refresh
                  </Button>
                  {groups.length > 0 && (
                    <Button kind="ghost" onClick={allOpen ? collapseAll : expandAll}>
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </Button>
                  )}
                </div>
              </>
            }
          />
        </>
      )}
    </div>
  )
}
